import { UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { Component, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { Observable, of, } from 'rxjs';
import { takeWhile, map, switchMap } from 'rxjs';
import { ConfigSection } from '../config-section';
import { SystemSettingsDto } from '@admin/models/mojo-system-settings-model';
import { MojoSystemSettingsService } from '@feature/mojo/services/mojo-system-settings.service';
import { DisplayMode } from '@feature/user-preferences/components/periodic-email-settings/common';
import { PeriodicEmailSettingsComponent } from '@feature/user-preferences/components/periodic-email-settings/periodic-email-settings.component';
import { IPeriodicEmailSettings } from '@feature/user-preferences/setting-types/periodic-email-settings';
import { IPeriodicEmailSettingsTuple } from '@feature/user-preferences/setting-types/common';

@Component({
  selector: 'mojo-mojo-activity-settings',
  templateUrl: './activity-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Default
})
export class ActivitySettingsComponent extends ConfigSection<SystemSettingsDto> {

  formsToindividualsDefaultOptions: Map<string, string> = new Map([]);
  formsToIndividualsOptions: Map<string, string> = new Map([
    ['NotAllowed', 'Not allowed (all designs)'],
    ['Allowed', 'Allowed (all designs)'],
    ['Required', 'Required (all designs)'],
    ['SetPerFormDesign', 'Set per Form Design']
  ]);
  DM = DisplayMode;
  @ViewChild('periodicEmailSettings') readonly periodicEmailSettingsComponent: PeriodicEmailSettingsComponent;
  periodicEmailSettings: IPeriodicEmailSettings
  periodicEmailSettingsTuple: IPeriodicEmailSettingsTuple

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly systemSettings: MojoSystemSettingsService) {
    super();
    this.populateIndividualsDefaultOptions();
  }

  initialiseConfig(): Observable<SystemSettingsDto> {
    return this.systemSettings.getSystemSettings()
      .pipe(
        switchMap(settings => {
          return of(settings);
        })
      );
  }

  createForm(settings: SystemSettingsDto): void {
    this.form = this.formBuilder.group({
      ShowWorkGroups: [settings.ShowWorkgroups],
      EnableFlexibleWorkgroups: [settings.EnableFlexibleWorkgroups],
      AssigningFormsToIndividuals: [settings.AssigningFormsToIndividuals],
      AssigningFormsToIndividualsDefault: [settings.AssigningFormsToIndividualsDefault],
      UpdateFormsDesignsNow: [settings.UpdateFormsDesignsNow],
      SendEmailOnFormAssignment: [settings.SendEmailOnFormAssignment]
    });

    this.periodicEmailSettingsTuple = <IPeriodicEmailSettingsTuple> {
      SystemDefaults: settings.PeriodicEmailSettings,
      UserPreferences: null,
    }    

    this.form.get('ShowWorkGroups').valueChanges.pipe(
      takeWhile(() => this.alive)
    ).subscribe(value => {
      this.onShowWorkGroupsValueChange(<boolean>value);
    });

    this.form.get('AssigningFormsToIndividuals').valueChanges.pipe(
      takeWhile(() => this.alive)
    ).subscribe(value => {
      this.onFormToIndividualsAssignmentChange(value);
    });

    this.initialiseFormState(settings);
  }

  submit(): Observable<boolean> {
    const settings: SystemSettingsDto = this.form.getRawValue();
    settings.Id = this.originalConfig.Id;
    settings.PeriodicEmailSettings = this.periodicEmailSettings
    return this.systemSettings.saveSystemSettings(settings).pipe(map(() => true));
  }

  private initialiseFormState(settings: SystemSettingsDto): void {
    this.onShowWorkGroupsValueChange(settings.ShowWorkgroups);
    this.onFormToIndividualsAssignmentChange(settings.AssigningFormsToIndividuals);
  }

  private onShowWorkGroupsValueChange(value: boolean): void {
    const enableFlexibleWorkgroups: AbstractControl = this.form.get('EnableFlexibleWorkgroups');
    // Disable the "Flexible Workgroups" toogle button when "Show Workgroups" is false
    if (value === true) {
      enableFlexibleWorkgroups.enable();
    } else {
      enableFlexibleWorkgroups.setValue(false);
      enableFlexibleWorkgroups.disable();
    }
  }

  private onFormToIndividualsAssignmentChange(value: string): void {
    const updateFormsDesignsNowControl: AbstractControl = this.form.get('UpdateFormsDesignsNow');
    const individualsDefaultControl: AbstractControl = this.form.get('AssigningFormsToIndividualsDefault');
    if (value === 'SetPerFormDesign') {
      // Populate the "Assigning Forms to Individuals (Default)" dropdown
      if (individualsDefaultControl.disabled) {
        this.populateIndividualsDefaultOptions();
        individualsDefaultControl.enable();
        individualsDefaultControl.setValue(this.formsToindividualsDefaultOptions.keys().next().value);
      }
      // Enable the "Update form/designs now" checkbox
      updateFormsDesignsNowControl.enable();
    } else {
      // Empty and disable the "Assigning Forms to Individuals (Default)" dropdown
      this.formsToindividualsDefaultOptions = new Map([]);
      individualsDefaultControl.setValue('');
      individualsDefaultControl.disable();
      // Disable the "Update form/designs now" checkbox
      updateFormsDesignsNowControl.disable();
    }
    // Automatically tick "Update form/designs now" when "Assign Forms to Individuals" is changed
    if (!this.loading) {
      if (value === this.originalConfig.AssigningFormsToIndividuals) {
        updateFormsDesignsNowControl.setValue(false);
      } else {
        updateFormsDesignsNowControl.setValue(true);
      }
    }
  }

  private populateIndividualsDefaultOptions(): void {
    this.formsToindividualsDefaultOptions = new Map([
      ['NotAllowed', 'Not Allowed'],
      ['Allowed', 'Allowed'],
      ['Required', 'Required']
    ]);
  }

  periodicEmailSettingsChanged(e) {
    this.periodicEmailSettings = e
  }

  get modified(): boolean {
    return (this.form && !this.form.pristine) || 
           (this.periodicEmailSettingsComponent && this.periodicEmailSettingsComponent.changed());
  }

}
